import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import useUserStore from '@/store/useUserStore'
import MicrosoftIcon from '@/components/common/MicrosoftIcon'
import GoogleIcon from '@/components/common/GoogleIcon'
import Footer from '@/components/common/Footer'
import { getAuthProviders, initMicrosoftAuth, initGoogleAuth } from '@/services/api'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setAuthToken, updateUser } = useUserStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false)
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [loadingProviders, setLoadingProviders] = useState(true)

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await getAuthProviders()
        if (data.providers) {
          if (data.providers.some(p => p.name === 'microsoft')) {
            setMicrosoftEnabled(true)
          }
          if (data.providers.some(p => p.name === 'google')) {
            setGoogleEnabled(true)
          }
        }
      } catch (err) {
        // OAuth not available, just hide the button
        console.log('OAuth providers not available')
      } finally {
        setLoadingProviders(false)
      }
    }
    fetchProviders()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok) {
        setAuthToken(data.token)
        updateUser(data.user)
        toast.success(`Welcome, ${data.user.email}!`, {
          description: 'Login successful'
        })

        // Check for returnTo URL parameter from 401 redirect
        const returnTo = searchParams.get('returnTo')
        if (returnTo) {
          navigate(returnTo)
        } else {
          navigate('/projects')
        }
      } else {
        toast.error(data.error || 'Login failed')
      }
    } catch (err) {
      toast.error('Unable to connect to server')
    } finally {
      setLoading(false)
    }
  }

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true)
      const returnTo = searchParams.get('returnTo') || '/projects'
      const data = await initMicrosoftAuth(returnTo)

      // Store state in sessionStorage for validation in callback
      sessionStorage.setItem('oauth_state', data.state)

      // Redirect to Microsoft
      window.location.href = data.authUrl
    } catch (error) {
      toast.error('Failed to initialize Microsoft login')
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setLoading(true)
      const returnTo = searchParams.get('returnTo') || '/projects'
      const data = await initGoogleAuth(returnTo)

      // Store state in sessionStorage for validation in callback
      sessionStorage.setItem('oauth_state', data.state)

      // Redirect to Google
      window.location.href = data.authUrl
    } catch (error) {
      toast.error('Failed to initialize Google login')
      setLoading(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Login</CardTitle>
          <CardDescription>Enter your credentials to access your account</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="text-right">
                <Link
                  to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}
                  className="text-xs text-muted-foreground hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-gray-800"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            {!loadingProviders && (microsoftEnabled || googleEnabled) && (
              <>
                <div className="relative w-full">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                {microsoftEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleMicrosoftLogin}
                    disabled={loading}
                    data-testid="microsoft-login-button"
                  >
                    <MicrosoftIcon className="mr-2 h-4 w-4" />
                    Sign in with Microsoft
                  </Button>
                )}

                {googleEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    data-testid="google-login-button"
                  >
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Sign in with Google
                  </Button>
                )}
              </>
            )}
            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline" data-testid="create-account-link">
                Create one
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
      <Footer />
    </div>
  )
}

export default Login
